<template>
  <div class="configuration-tab">
    <app-loading :loading="isLoading" is-background />

    <ValidationObserver v-if="!isLoading" v-slot="v">
      <b-form id="edit-award-form" @submit.prevent="v.handleSubmit(onSubmit)">
        <div class="form-container">
          <!-- Basic Details -->
          <basic-details-section
            :name="awardForm.name"
            :selected-division-ids="selectedDivisionIds"
            :division-options="divisionOptions"
            @update:name="awardForm.name = $event"
            @update:selectedDivisionIds="onDivisionsChange"
          >
            <template #after-divisions>
              <hr />

              <div class="company-values-container">
                <div class="label-row">
                  <div class="label">
                    Add your company values (optional)
                    <small>
                      Tag relevant values to help employees understand what behaviours this award recognises. You can
                      use our suggestions or customise them to match your organisation's values.
                    </small>
                  </div>
                  <b-form-checkbox v-model="companyValuesEnabled" switch />
                </div>
                <div v-if="companyValuesEnabled">
                  <create-company-values-modal v-model="companyValues" />
                </div>
              </div>

              <hr />

              <div class="short-description-container">
                <div class="label">
                  Short description (optional)
                  <small>
                    A short description with extra detail about this award — for example its location or budget source.
                  </small>
                </div>
                <app-input
                  v-model="shortDescription"
                  type="text"
                  maxlength="500"
                  placeholder="Add a short description (optional)"
                />
              </div>
            </template>
          </basic-details-section>

          <!-- Appearance -->
          <image-upload-section
            :badge-collectible="false"
            banner-image-type="open_banner"
            :banner-images="bannerImages"
            :banner-images-error="bannerImagesError"
            :badge-file="badgeFile"
            :badge-preview-url="badgePreviewUrl"
            :allowed-image-types="allowedImageTypes"
            :max-size-bytes="MAX_BANNER_SIZE_BYTES"
            @add:bannerImages="addBannerImages"
            @add:pendingBannerFiles="addPendingBannerFiles"
            @remove:bannerImage="removeBannerImage"
            @update:badgeFile="onBadgeFileUpdate"
            @update:badgePreviewUrl="onBadgePreviewUrlUpdate"
            @gallerySelect:badge="onBadgeGallerySelect"
            @remove:badge="onBadgeRemove"
          />

          <!-- Award Details -->
          <div>
            <h4>Award details</h4>
            <sme-card large>
              <div class="card-container">
                <div class="label-row image-choice-row">
                  <div class="label">
                    Let the sender choose the image
                    <small>
                      When on, the person giving this award picks which banner image is shown. Requires at least two
                      images.
                    </small>
                  </div>
                  <b-form-checkbox v-model="allowNominatorImageChoice" switch :disabled="bannerImages.length < 2" />
                </div>
              </div>
            </sme-card>
          </div>
        </div>

        <!-- Footer -->
        <div class="form-footer">
          <small v-if="errorMessage" class="text-danger mr-3">{{ errorMessage }}</small>
          <b-button variant="outline-secondary" class="mr-2" :disabled="saving" @click="resetForm"> Reset </b-button>
          <b-button type="submit" variant="primary" :disabled="saving || !hasChanges">
            <b-spinner v-if="saving" class="mr-2" small />
            {{ saving ? 'Saving...' : 'Save changes' }}
          </b-button>
        </div>
      </b-form>
    </ValidationObserver>
  </div>
</template>

<script setup>
import { ValidationObserver } from 'vee-validate';
import { ref, computed, watch, onMounted } from 'vue';
import ApiClient from '@/ApiClient';
import { RECOGNITION_APPROVAL_TYPES } from '@/pages/recognition/constants';
import { useAwardForm } from '@/pages/recognition/composables/useAwardForm';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import CreateCompanyValuesModal from '@/pages/recognition/components/CreateCompanyValuesModal.vue';
import useToast from '@/composables/useToast';
import BasicDetailsSection from '@/pages/recognition/components/BasicDetailsSection.vue';
import ImageUploadSection from '@/pages/recognition/components/ImageUploadSection.vue';

const props = defineProps({
  program: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['updated']);

// Use shared composable
const {
  awardForm,
  selectedDivisionIds,
  divisionOptions,
  enabledDivisions,
  shortDescription,
  bannerImages,
  bannerImagesError,
  allowNominatorImageChoice,
  addBannerImages,
  addPendingBannerFiles,
  removeBannerImage,
  badgeFile,
  badgePreviewUrl,
  allowedImageTypes,
  MAX_BANNER_SIZE_BYTES,
  onBadgeFileUpdate,
  onBadgePreviewUrlUpdate,
  onBadgeGallerySelect,
  onBadgeRemove,
  badgeS3Key,
  saving,
  errorMessage,
  loadDivisions,
  setupCommonWatchers,
  buildBaseProperties,
  getImageData,
} = useAwardForm();

// Open-specific state
const companyValuesEnabled = ref(false);
const companyValues = ref([]);
const isLoading = ref(true);

// Track original values for change detection
const originalValues = ref({});

const { doToast, TOAST_VARIANT } = useToast();

// Detect if form has changes
const hasChanges = computed(() => {
  const current = getCurrentFormState();
  return JSON.stringify(current) !== JSON.stringify(originalValues.value);
});

/**
 * Get current form state for comparison
 */
const getCurrentFormState = () => ({
  name: awardForm.value.name,
  selectedDivisionIds: [...(selectedDivisionIds.value || [])].sort(),
  companyValuesEnabled: companyValuesEnabled.value,
  companyValues: [...(companyValues.value || [])].sort(),
  shortDescription: shortDescription.value,
  badgeFileName: badgeFile.value?.name ?? null,
  badgeS3Key: badgeS3Key.value,
  bannerImageKeys: bannerImages.value.map(i => i.s3Key).join(','),
  allowNominatorImageChoice: allowNominatorImageChoice.value,
});

/**
 * Populate form from program data
 */
const populateFromProgram = program => {
  if (!program) {
    return;
  }

  // Basic details
  awardForm.value.name = program.program_name || '';

  // Divisions - API returns array of UUIDs or empty array for "all"
  const divisions = program.enabled_divisions || [];
  selectedDivisionIds.value = divisions.length > 0 ? divisions : [];

  // Company values
  const programCompanyValues = program.properties?.company_values || [];
  if (programCompanyValues.length > 0) {
    companyValuesEnabled.value = true;
    companyValues.value = programCompanyValues.map(v => v.name || v);
  } else {
    companyValuesEnabled.value = false;
    companyValues.value = [];
  }

  // Short description (HERO-2972)
  shortDescription.value = program.properties?.short_description || '';

  // Images - hydrate banner list + existing badge
  const bannerKeys = program.banner_s3_keys || (program.banner_s3_key ? [program.banner_s3_key] : []);
  const bannerUrls =
    program.banner_presigned_urls || (program.banner_presigned_url ? [program.banner_presigned_url] : []);
  bannerImages.value = bannerKeys.map((s3Key, i) => ({ id: s3Key, s3Key, file: null, url: bannerUrls[i] || '' }));
  allowNominatorImageChoice.value = Boolean(program.properties?.allow_nominator_image_choice);
  badgePreviewUrl.value = program.badge_presigned_url || '';
  badgeS3Key.value = program.badge_s3_key || '';
  // Clear any previously selected file
  onBadgeFileUpdate(null);

  // Store original values for change detection
  originalValues.value = getCurrentFormState();
};

/**
 * Reset form to original program values
 */
const resetForm = () => {
  populateFromProgram(props.program);
  // Clear any newly selected image file
  onBadgeFileUpdate(null);
  errorMessage.value = '';
};

const buildProperties = () => {
  const properties = buildBaseProperties();

  // Open awards are always instant, so clear any approvers left on a program
  // configured before that rule (properties merge server-side).
  properties.approvers = null;

  if (companyValuesEnabled.value) {
    const values = (companyValues.value || []).map(v => String(v || '').trim()).filter(Boolean);
    if (values.length) {
      properties.company_values = values.map(name => ({ name }));
    }
  }

  return properties;
};

const buildPayload = properties => ({
  program_name: awardForm.value.name,
  approval_type: RECOGNITION_APPROVAL_TYPES.NONE,
  show_badge: false,
  enabled_divisions: enabledDivisions.value,
  properties,
});

const onSubmit = async () => {
  errorMessage.value = '';
  saving.value = true;

  try {
    const payload = buildPayload(buildProperties());
    const { files, s3Keys } = getImageData();

    await ApiClient.updateRecognitionProgram(props.program.recognition_award_program_id, payload, files, s3Keys);

    doToast('Success', 'Award updated successfully', TOAST_VARIANT.SUCCESS);

    // Update original values to reflect saved state
    originalValues.value = getCurrentFormState();

    emit('updated');
  } catch (e) {
    errorMessage.value = e?.response?.data?.message || e?.variables?.message || e?.message || 'Failed to update award';
  } finally {
    saving.value = false;
  }
};

const onDivisionsChange = ids => {
  // Open awards have no division-scoped nominator picker, so nothing is pruned on change.
  selectedDivisionIds.value = ids;
};

onMounted(async () => {
  isLoading.value = true;
  try {
    await loadDivisions();
    setupCommonWatchers();
    populateFromProgram(props.program);
  } finally {
    isLoading.value = false;
  }
});

// Watch for program prop changes (e.g., when refreshed from parent)
watch(
  () => props.program,
  newProgram => {
    if (newProgram) {
      populateFromProgram(newProgram);
    }
  },
);
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/award-form';

.configuration-tab {
  position: relative;
  min-height: 200px;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--palette-color-default-lighten-90);
}
</style>
